import * as React from 'react';
import {Query} from 'react-apollo';
import {Helmet} from 'react-helmet';
import {connect} from 'react-redux';

// @ts-expect-error
import memoize from 'memoize-one';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {headTitle} from 'shells/document/head-title';
import type {DateHeader} from 'shells/email-row-item/types';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';

import type {
    GetReceivedEmails as GetReceivedEmailsQuery,
    GetReceivedEmailsVariables as GetReceivedEmailsQueryVariables,
    Email as EmailFragment,
    UpdateEmail as UpdateEmailMutation,
    DeleteEmail as DeleteEmailMutation,
} from 'nutshell-graphql-types';

import {BannerCueCards} from '../../../glossary/layout/banner-cue-cards';
import {useGetCueCardsForLocation} from '../../../glossary/layout/helpers';

import {nutApolloClient} from '../../../../apollo-client';

import {openReplyModal} from '../../email-reply-actions';
import {EmailSetupCta} from '../../email-setup-cta';

import {EmailList} from '../email-list';
import GET_RECEIVED_EMAILS from '../graphql/queries/get-received-emails.graphql';
import {addHeaders, ERROR_STATE} from '../utils';

import {InboxRowItem} from './inbox-row-item';
import {SyncStatusBanner} from './sync-status-banner';

import styles from '../email-list.module.css';

const LIMIT = 50;

type ComponentProps = {
    isLoading: boolean;
    emails: EmailFragment[];
    hasNextPage: boolean;
    loadNextPage: () => Promise<any>;
    errorMessage: string | null | undefined;
};

type DispatchProps = {
    openModal: (replyToEmailId: string) => void;
};

type Props = ComponentProps & DispatchProps;

function InboxBannerCueCardsSection() {
    const hasBannerCueCard = Boolean(useGetCueCardsForLocation(window.location.pathname, 'banner'));

    return (
        <>
            {hasBannerCueCard && (
                <div className='px-16 pt-16'>
                    <BannerCueCards />
                </div>
            )}
        </>
    );
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        openModal: (replyToEmailId: string) => {
            dispatch(openReplyModal({replyToEmailId}));
        },
    };
}

export class InboxComponent extends React.PureComponent<Props, null> {
    addHeadersToEmails: (arg1: EmailFragment[]) => EmailFragment | DateHeader[];

    constructor() {
        // @ts-expect-error
        super();
        // @ts-expect-error
        this.addHeadersToEmails = memoize((emails) => addHeaders(emails));
    }

    render() {
        const emails = this.addHeadersToEmails(this.props.emails);
        const isEmailConfigured = NutClientConfig.hasConfiguredEmail;

        const inboxEmptyStateConfigured = (
            <EmptyStateSmall
                title='Your inbox is currently empty'
                subtitle='Here you can view all of your email that you’ve imported, either through bcc@nutshell.com or through Nutshell mail sync.'
                icon={<img alt='' src='/include/images/email-drip/email-inbox_2x.png' />}
            />
        );
        const inboxEmptyStateUnconfigured = (
            <EmptyStateLarge
                title='All your email in one place'
                subtitle='Send, schedule, and read email, all within Nutshell'
                detailTextBullets={[
                    'Create people in Nutshell from emails in your inbox with just a click.',
                    'If you’d like, you can share emails with the rest of your company. They’ll show up on the related person, company, or lead, so that your entire team is in the loop.',
                ]}
                cta={<EmailSetupCta />}
                icon={
                    <img
                        alt=''
                        src='/include/images/email-marketing/email_inbox_empty_state.png'
                        className={styles['large-icon']}
                    />
                }
            />
        );

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle('Inbox')}</title>
                </Helmet>
                <InboxBannerCueCardsSection />
                <SyncStatusBanner />
                <EmailList
                    isRequesting={this.props.isLoading}
                    onFetchMoreRows={this.props.loadNextPage}
                    hasNextPage={this.props.hasNextPage}
                    // @ts-expect-error
                    collection={emails}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(this.props.errorMessage),
                            component: ERROR_STATE,
                        },
                        {
                            shouldRender: () =>
                                // @ts-expect-error
                                emails.length === 0 && !this.props.isLoading && !isEmailConfigured,
                            component: inboxEmptyStateUnconfigured,
                        },
                        {
                            // @ts-expect-error
                            shouldRender: () => emails.length === 0 && !this.props.isLoading,
                            component: inboxEmptyStateConfigured,
                        },
                    ]}
                    renderRow={(emailItem: EmailFragment, isFirstOfDate: boolean) => {
                        return (
                            <InboxRowItem
                                key={emailItem.id}
                                email={emailItem}
                                isFirstOfDate={isFirstOfDate}
                                onEmailSharedChange={updateCacheFromUpdate}
                                onEmailDeleted={updateCacheFromDelete}
                                onReply={
                                    emailItem.isReplyable
                                        ? () => {
                                              this.props.openModal(emailItem.id);
                                          }
                                        : undefined
                                }
                            />
                        );
                    }}
                    hasError={false}
                    onClearListError={() => {}}
                />
            </React.Fragment>
        );
    }
}

function updateCacheFromDelete(
    // @ts-expect-error
    proxy,
    mutationResult: {
        data?: DeleteEmailMutation;
    }
) {
    const deletedEmailId = mutationResult.data && mutationResult.data.emailDelete.email.id;
    if (!deletedEmailId) return;

    // Read the data from our cache for this query.
    const cacheResult: GetReceivedEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_RECEIVED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!cacheResult) return;

    // Remove deleted items from cache
    cacheResult.receivedEmails.edges = cacheResult.receivedEmails.edges.filter(
        (edge) => edge.node && edge.node.id && edge.node.id !== deletedEmailId
    );

    // Write our data back to the cache.
    nutApolloClient.writeQuery({
        query: GET_RECEIVED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheResult,
    });
}

function updateCacheFromUpdate(
    // @ts-expect-error
    proxy,
    mutationResult: {
        data?: UpdateEmailMutation;
    }
) {
    const updatedEmail =
        mutationResult.data && mutationResult.data.emailShareAndCreateContacts.email;
    if (!updatedEmail) return;
    const updatedEmailId = updatedEmail.id;

    // Read the data from our cache for this query.
    const cacheResult: GetReceivedEmailsQuery | null | undefined = nutApolloClient.readQuery({
        query: GET_RECEIVED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!cacheResult) return;

    // Update our new email item
    cacheResult.receivedEmails.edges = cacheResult.receivedEmails.edges.map((edge) => {
        if (edge.node && edge.node.id && edge.node.id === updatedEmailId) {
            return {
                __typename: 'EmailsEdge',
                node: {...edge.node, isShared: updatedEmail.isShared},
                cursor: edge.cursor,
            };
        }

        return edge;
    });

    // Write our data back to the cache.
    nutApolloClient.writeQuery({
        query: GET_RECEIVED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheResult,
    });
}

class ReceivedEmailQuery extends Query<GetReceivedEmailsQuery, GetReceivedEmailsQueryVariables> {}

const InboxConnected = (props: DispatchProps) => (
    <ReceivedEmailQuery
        query={GET_RECEIVED_EMAILS}
        // isLoading only changes on `loadNextPage` if this option is true
        notifyOnNetworkStatusChange={true}
        variables={{limit: LIMIT}}
        fetchPolicy='network-only'
    >
        {({data, loading, refetch, networkStatus, fetchMore, error}) => {
            const receivedEmails = data ? data.receivedEmails : null;

            return (
                // @ts-expect-error
                <InboxComponent
                    isLoading={loading}
                    // @ts-expect-error
                    onRefetch={refetch}
                    // @ts-expect-error
                    errorMessage={safelyGetGraphQLErrorMessage(error)}
                    networkStatus={networkStatus}
                    hasNextPage={receivedEmails ? receivedEmails.pageInfo.hasNextPage : true}
                    emails={receivedEmails ? translateConnectionObjectToArray(receivedEmails) : []}
                    loadNextPage={() =>
                        fetchMore({
                            variables: {
                                limit: LIMIT,
                                cursor: receivedEmails ? receivedEmails.pageInfo.endCursor : null,
                            },
                            updateQuery: (previousResult, {fetchMoreResult}) => {
                                if (!fetchMoreResult) return previousResult;

                                const previousEdges = previousResult.receivedEmails.edges;
                                const newEdges = fetchMoreResult.receivedEmails.edges;
                                const newTotalCount = fetchMoreResult.receivedEmails.totalCount;
                                const newPageInfo = fetchMoreResult.receivedEmails.pageInfo;

                                return {
                                    // Put the new emails at the end of the list and update `pageInfo`
                                    // so we have the new `endCursor` and `hasNextPage` values
                                    receivedEmails: {
                                        __typename: 'EmailsConnection',
                                        totalCount: newTotalCount,
                                        pageInfo: newPageInfo,
                                        edges: [...previousEdges, ...newEdges],
                                    },
                                };
                            },
                        })
                    }
                    openModal={props.openModal}
                />
            );
        }}
    </ReceivedEmailQuery>
);

// @ts-expect-error
const connector = connect<_, Record<any, any>, _, _, _, _>(null, mapDispatchToProps);
export const Inbox = connector(InboxConnected);
